import React, { useEffect, useState, useMemo } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { get } from '../../../helpers/httpClient';
import { toast } from 'react-toastify';
import { GrGallery, GrDocumentText, GrCatalogOption } from "react-icons/gr";

function getTypeIcon(t) {
  const type = (t || '').toLowerCase();
  switch (type) {
    case 'article':
      return <GrDocumentText size={14} aria-label="article" />;
    case 'catalog':
      return <GrCatalogOption size={14} aria-label="catalog" />;
    case 'gallery':
    default:
      return <GrGallery size={14} aria-label='gallery' />;
  }
}

function Structure({ website /*, onUpdated */ }) {
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({}); // key: parent slug -> bool

  useEffect(() => {
    let isMounted = true;
    async function loadPages() {
      if (!website?.id) return;
      try {
        setLoading(true);
        setError('');
        const resp = await get(`websites/${website.id}/pages`);
        // Accept array or object with pages property
        const list = Array.isArray(resp) ? resp : (resp?.pages || []);
        if (isMounted) setPages(list);
      } catch (e) {
        if (isMounted) toast.error('Failed to load pages.');
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    loadPages();
    return () => { isMounted = false; };
  }, [website?.id]);

  if (!website) {
    return <Alert variant="warning">Website not found.</Alert>;
  }

  const goToCreate = () => {
    navigate(`/websites/${website.id}/structure/new`);
  };

  const goToEdit = (slug) => {
    navigate(`/websites/${website.id}/structure/${slug}/edit`);
  };

  const toggleExpand = (slug) => {
    setExpanded((prev) => ({ ...prev, [slug]: !prev[slug] }));
  };

  const addEntity = (parentType = null, parentId = null) => {
    let parentParam = parentId ? `?parent=${encodeURIComponent(parentId)}` : '';
    if (parentType.toLowerCase() === 'gallery') {
       parentParam += '&type=gallery-item';
    }
    navigate(`/websites/${website.id}/structure/new${parentParam}`);
  };


  const renderTypeCell = (p) => {
    const icon = getTypeIcon(p.type);
    if ((p.type || '').toLowerCase() !== 'catalog') {
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {icon}
          <span>/{p.slug}</span>
        </span>
      );
    }
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <button
          type="button"
          onClick={() => toggleExpand(p.slug)}
          title="Show children"
          aria-label={expanded[p.id] ? 'Collapse children' : 'Expand children'}
          style={{ background: 'transparent', border: 'none', padding: 0, display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}
        >
          {icon}
        </button>
        <span>/{p.slug}</span>
      </span>
    );
  };

  function StructureTree({ pages, expanded, onToggle, onEdit, onAdd }) {
    const keyFor = (parent) => (parent === null || parent === undefined || parent === '' ? '__root__' : parent);
    const pagesByParent = useMemo(() => {
      const map = {};
      for (const pg of pages || []) {
        const k = keyFor(pg.parent);
        if (!map[k]) map[k] = [];
        map[k].push(pg);
      }

      Object.values(map).forEach(arr => arr.sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id.localeCompare(b.id)));
      return map;
    }, [pages]);

    const NodeRow = ({ p, level }) => {
      const type = (p.type || '').toLowerCase();
      const isBranch = type === 'catalog' || type === 'gallery';
      const hasChildren = (pagesByParent[keyFor(p.id)] || []).length > 0;
      const isOpen = !!expanded[p.id];
      return (
        <div className="structure-node">
          <div className="structure-node-header">
            <div className="structure-node-info">
              <div style={{ width: level * 14 }} aria-hidden />
              {isBranch ? (
                <button
                  type="button"
                  onClick={() => onToggle(p.id)}
                  title={isOpen ? 'Collapse' : 'Expand'}
                  aria-label={isOpen ? 'Collapse children' : 'Expand children'}
                  style={{ background: 'transparent', border: 'none', padding: 0, display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}
                >
                  {getTypeIcon(p.type)}
                </button>
              ) : (
                <span>{getTypeIcon(p.type)}</span>
              )}
              <span>/{p.slug}</span>
              <span>— {p.title}</span>
            </div>
            <div className="structure-node-info">
              <Button size="sm" className="primary-button m-0" onClick={() => onEdit(p.id)}>Edit</Button>
              {isBranch && (
                <Button className="primary-button m-0" onClick={() => onAdd(p.type, p.id)}>Add</Button>
              )}
            </div>
          </div>
          {isBranch && isOpen && hasChildren && (
            <div style={{ marginTop: 6, marginLeft: 14, paddingLeft: 8, borderLeft: '1px dashed #e2e8f0' }}>
              <TreeLevel parentSlug={p.id} level={level + 1} />
            </div>
          )}
        </div>
      );
    };

    const TreeLevel = ({ parentSlug, level = 0 }) => {
      const k = keyFor(parentSlug);
      const nodes = pagesByParent[k] || [];
      if (nodes.length === 0) return null;
      return (
        <div>
          {nodes.map((p) => (
            <NodeRow key={p.slug} p={p} level={level} />
          ))}
        </div>
      );
    };

    return (
      <div>
        <TreeLevel parentSlug={null} level={0} />
      </div>
    );
  }

  return (
    <div>
      <p style={{ marginBottom: '0.75rem', color: '#64748b' }}>Create new content entities for your site.</p>
      <div className="form-row-horizontal" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', width: '100%' }}>
          <Button className="primary-button" size="sm" onClick={goToCreate}>Add</Button>
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <h5>Pages</h5>
        <p style={{ margin: '6px 0 12px', color: '#64748b' }}>
          Manage your site's pages. Use the tree below to navigate and edit.
        </p>
        {error && <Alert variant="danger">{error}</Alert>}
        {loading ? (
          <div className="text-center my-3"><Spinner animation="border" size="sm" /></div>
        ) : (
          <>
            <StructureTree pages={pages} expanded={expanded} onToggle={toggleExpand} onEdit={goToEdit} onAdd={addEntity} />
          </>
        )}
      </div>
    </div>
  );
}

export default Structure;
